import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { Clipboard, PlayCircleFill } from "react-bootstrap-icons";
import { apiGet, getFilePreviewUrl, MEDIA_CATEGORIES } from "./contentApi";

// "freetime/lesson_1.mp3" -> "lesson_1": what to type as "File name on server".
const fileNameWithoutExtension = (path) => path.split("/").pop().replace(/\.(mp3|mp4)$/i, "");

const FilePreview = ({ path, onClose }) => {
    const [src, setSrc] = useState(null);
    const [error, setError] = useState("");
    const isVideo = path.toLowerCase().endsWith(".mp4");

    useEffect(() => {
        let cancelled = false;
        setSrc(null);
        setError("");
        getFilePreviewUrl(path)
            .then((url) => !cancelled && setSrc(url))
            .catch((err) => !cancelled && setError(err.message));
        return () => {
            cancelled = true;
        };
    }, [path]);

    return (
        <Card className="mb-3 shadow-sm">
            <Card.Body>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <code className="small flex-grow-1 text-break">{path}</code>
                    <Button size="sm" variant="outline-secondary" onClick={onClose}>Close</Button>
                </div>
                {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
                {!src && !error && <Spinner animation="border" size="sm" />}
                {src && (isVideo ? (
                    <video src={src} controls autoPlay className="w-100 rounded" style={{ maxHeight: 360 }} />
                ) : (
                    <audio src={src} controls autoPlay className="w-100" />
                ))}
            </Card.Body>
        </Card>
    );
};

const categoryLabel = (value) => MEDIA_CATEGORIES.find((c) => c.value === value)?.label ?? value;

const formatSize = (bytes) => {
    if (bytes == null || bytes < 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Report of files on disk without a media item, and media items whose file is missing. */
const MediaFilesCheckModal = ({ show, levels, onClose, onEditItem }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [previewPath, setPreviewPath] = useState(null);
    const [copiedPath, setCopiedPath] = useState(null);

    const copyFileName = (path) => {
        navigator.clipboard?.writeText(fileNameWithoutExtension(path)).then(() => {
            setCopiedPath(path);
            setTimeout(() => setCopiedPath(null), 1500);
        });
    };

    const levelTitle = (levelId) => levels.find((l) => l.id === levelId)?.title ?? levelId;

    const runCheck = useCallback(() => {
        setLoading(true);
        setError("");
        apiGet("/admin/content/media-files/check")
            .then(setReport)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (show) runCheck();
        else setPreviewPath(null);
    }, [show, runCheck]);

    return (
        <Modal show={show} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5">Check media files</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : report && (
                    <>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <Badge bg="secondary">{report.filesOnDisk} files on disk</Badge>
                            <Badge bg="secondary">{report.itemsWithFile} media items with a file</Badge>
                            <Badge bg={report.unlinkedFiles.length ? "warning" : "success"} text={report.unlinkedFiles.length ? "dark" : undefined}>
                                {report.unlinkedFiles.length} unlinked files
                            </Badge>
                            <Badge bg={report.missingFiles.length ? "danger" : "success"}>
                                {report.missingFiles.length} missing files
                            </Badge>
                        </div>

                        <Tabs defaultActiveKey="missing" id="media-files-check-tabs" className="mb-3">
                            <Tab eventKey="missing" title={`Missing files (${report.missingFiles.length})`}>
                                <p className="small text-muted">
                                    Media items whose file is not on the server. They don't play in the app.
                                </p>
                                {report.missingFiles.length === 0 ? (
                                    <p className="text-success">Every media item's file exists.</p>
                                ) : (
                                    <Table bordered hover responsive size="sm" className="align-middle">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Level / type / unit</th>
                                                <th>Expected file</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.missingFiles.map(({ expectedPath, item }) => (
                                                <tr key={item.id}>
                                                    <td style={{ whiteSpace: "pre-wrap" }}>
                                                        <span className="text-muted small">#{item.id}</span> {item.title}
                                                    </td>
                                                    <td className="small">
                                                        {levelTitle(item.levelId)} · {categoryLabel(item.category)}
                                                        {item.unitTitle && <> · Unit {item.unitScreenOrder}</>}
                                                    </td>
                                                    <td><code className="small">{expectedPath}</code></td>
                                                    <td>
                                                        <Button size="sm" variant="outline-primary" onClick={() => onEditItem(item)}>
                                                            Edit
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab>

                            <Tab eventKey="unlinked" title={`Unlinked files (${report.unlinkedFiles.length})`}>
                                <p className="small text-muted">
                                    Files on the server that no media item points to, e.g. detached or replaced
                                    files, or files copied in by hand. Folders <code>upper/</code> are shared by
                                    the Upper-Intermediate and Advanced levels.
                                </p>
                                {previewPath && (
                                    <FilePreview path={previewPath} onClose={() => setPreviewPath(null)} />
                                )}

                                {report.unlinkedFiles.length === 0 ? (
                                    <p className="text-success">Every file on the server is linked to a media item.</p>
                                ) : (
                                    <Table bordered hover responsive size="sm" className="align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 90 }} />
                                                <th>File</th>
                                                <th style={{ width: 110 }}>Size</th>
                                                <th style={{ width: 190 }}>Last modified</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.unlinkedFiles.map((file) => (
                                                <tr key={file.path} className={previewPath === file.path ? "table-active" : ""}>
                                                    <td className="text-nowrap">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            title="Play"
                                                            className="me-1"
                                                            onClick={() => setPreviewPath(file.path)}
                                                        >
                                                            <PlayCircleFill />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant={copiedPath === file.path ? "success" : "outline-secondary"}
                                                            title="Copy the name to enter as 'File name on server'"
                                                            onClick={() => copyFileName(file.path)}
                                                        >
                                                            <Clipboard />
                                                        </Button>
                                                    </td>
                                                    <td>
                                                        <code className="small">{file.path}</code>
                                                        {copiedPath === file.path && (
                                                            <span className="small text-success ms-2">
                                                                Copied "{fileNameWithoutExtension(file.path)}"
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="small">{formatSize(file.size)}</td>
                                                    <td className="small">
                                                        {file.lastModified ? new Date(file.lastModified).toLocaleString() : "-"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab>
                        </Tabs>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={runCheck} disabled={loading}>Check again</Button>
                <Button variant="secondary" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MediaFilesCheckModal;
